import { useState } from "react";
import { Box, Button, Link, Alert } from "@mui/material";
import { Link as RouterLink, useSearchParams } from "react-router-dom";
import { LanguageItem, AuthPageLayout } from "../../lib/common";
import AsyncFormBox from "../../lib/common/AsyncFormBox";
import { CommonCalls } from "../../config/calls";

/**
 * Translates backend error codes into user-friendly messages for the email verification form.
 * @param {Error} error - Axios error from the API call.
 * @returns {string|{title: string, message: React.ReactNode}|undefined} Translated error message.
 */
function translateError(error) {
  const code = error.response?.data?.code;

  switch (code) {
    case "glint-js/invalidVerificationToken":
      return {
        title: "Platnost ověřovacího odkazu vypršela.",
        message: (
          <>
            Pro dokončení aktivace nastavte nové heslo na stránce{" "}
            <Link component={RouterLink} to={"/forgotPassword"}>
              Změna hesla
            </Link>
            . Po jeho změně bude účet automaticky aktivován.
          </>
        ),
      };
  }
}

function BackToSignInLink() {
  return (
    <Link component={RouterLink} to={"/signIn"} variant={"body2"}>
      <LanguageItem cs>Přejít na přihlášení</LanguageItem>
      {/* <LanguageItem en>Go to Sign In</LanguageItem> */}
    </Link>
  );
}

function InvalidTokenView() {
  return (
    <AuthPageLayout
      title={
        <>
          <LanguageItem cs>Neplatný odkaz</LanguageItem>
          {/* <LanguageItem en>Invalid Link</LanguageItem> */}
        </>
      }>
      <Box sx={{ mt: 3, textAlign: "center" }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          <LanguageItem cs>Odkaz pro ověření e-mailu je neplatný. Zkuste se zaregistrovat znovu.</LanguageItem>
          {/* <LanguageItem en>The email verification link is invalid. Please try registering again.</LanguageItem> */}
        </Alert>
        <Link component={RouterLink} to={"/signUp"} variant={"body2"}>
          <LanguageItem cs>Zpět na registraci</LanguageItem>
          {/* <LanguageItem en>Back to Sign Up</LanguageItem> */}
        </Link>
      </Box>
    </AuthPageLayout>
  );
}

function SuccessView() {
  return (
    <Box sx={{ mt: 3, maxWidth: 400, textAlign: "center" }}>
      <Alert severity="success" sx={{ mb: 2 }}>
        <LanguageItem cs>Váš e-mail byl úspěšně ověřen. Nyní se můžete přihlásit.</LanguageItem>
        {/* <LanguageItem en>Your email has been verified successfully. You can now sign in.</LanguageItem> */}
      </Alert>
      <BackToSignInLink />
    </Box>
  );
}

function VerifyEmail() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const [verified, setVerified] = useState(false);

  if (!token) {
    return <InvalidTokenView />;
  }

  /**
   * Sends the verification token to the backend.
   * @returns {Promise} API call promise.
   */
  function handleSubmit() {
    return CommonCalls.verifyRegistration({ token });
  }

  /**
   * Handles successful verification by showing a confirmation message.
   */
  function onSubmitDone() {
    setVerified(true);
  }

  return (
    <AuthPageLayout
      title={
        <>
          <LanguageItem cs>Ověření e-mailu</LanguageItem>
          {/* <LanguageItem en>Email Verification</LanguageItem> */}
        </>
      }>
      {verified && <SuccessView />}

      {!verified && (
        <AsyncFormBox
          onSubmit={handleSubmit}
          onSubmitDone={onSubmitDone}
          translateError={translateError}
          boxProps={{
            sx: { mt: 2, textAlign: "center" },
          }}>
          <Alert severity="info" sx={{ mb: 2 }}>
            <LanguageItem cs>Klikněte na tlačítko níže pro ověření vaší e-mailové adresy.</LanguageItem>
            {/* <LanguageItem en>Click the button below to verify your email address.</LanguageItem> */}
          </Alert>

          <Button type={"submit"} fullWidth variant={"contained"} sx={{ mt: 1, mb: 2 }}>
            <LanguageItem cs>Ověřit e-mail</LanguageItem>
            {/* <LanguageItem en>Verify Email</LanguageItem> */}
          </Button>

          <BackToSignInLink />
        </AsyncFormBox>
      )}
    </AuthPageLayout>
  );
}

export default VerifyEmail;
